/**
 * Footnotes, and the promise they make.
 *
 * A superscript marker next to a figure is a claim that the working is written
 * down somewhere. This registry is what makes that true rather than decorative:
 * numbers are handed out in render order, and the assumptions section is built
 * from the same registry afterwards, so a marker cannot exist without an entry
 * and an entry cannot exist without a marker.
 *
 * One registry per render pass. Never module-level state — two renders would
 * keep incrementing and the numbering would drift from the page.
 */

import { esc } from './num.js';

export interface FootnoteEntry {
  n: number;
  /** Metric id. Also the anchor target in the assumptions section. */
  id: string;
}

export function createFootnotes() {
  const order: FootnoteEntry[] = [];
  const byId = new Map<string, number>();

  return {
    /**
     * The marker. Idempotent per id: citing the same metric twice on the page
     * reuses its number rather than minting a second one.
     */
    ref(id: string): string {
      let n = byId.get(id);
      if (n === undefined) {
        n = order.length + 1;
        byId.set(id, n);
        order.push({ n, id });
      }
      return `<a class="fn-ref" href="#assumption-${esc(id)}" id="fn-${esc(
        id,
      )}" aria-label="Assumption ${n}, jump to the work">${n}</a>`;
    },

    /** Everything cited, in the order it first appeared. */
    entries(): FootnoteEntry[] {
      return order;
    },

    numberOf(id: string): number | undefined {
      return byId.get(id);
    },
  };
}

export type Footnotes = ReturnType<typeof createFootnotes>;
